/*
The /calendar/events/ page: Live now, Upcoming by day (and by month for rumours of a month), past
events in a closed <details>, and two month calendars in the side column. Adds
schema.org Event data for the events that are still ahead. Needs page-events.hbs.
*/
import afterLoad from './afterLoad';
import {
    RADIO_URL,
    dateKey,
    dayAnchor,
    dayKeyOf,
    element,
    eventAnchor,
    fetchEvents,
    formatDay,
    formatStart,
    groupByDay,
    hasExactDay,
    isLive,
    isOver,
    isRumored,
    isUnconfirmedPassed,
    safeUrl,
} from './eventsData';
import { buildMonths } from './eventsMonths';

const EMPTY_NOTE = 'No events announced yet.';
const EMPTY_HINT = 'We cover big launches live on thehype radio.';
const ERROR_NOTE = 'The calendar is not available right now. Try again in a few minutes.';
// Pages already scrolled to their #anchor: a return from the back/forward cache restores
// the same DOM and the visitor's own scroll position, which must stay.
const revealedRoots = new WeakSet();

export default function eventsPage() {
    const root = document.querySelector('[data-events-page]');
    if (!root) {
        return;
    }
    afterLoad(() => showEvents(root));
}

async function showEvents(root) {
    const upcomingList = root.querySelector('[data-events-upcoming]');
    let events;
    try {
        events = await fetchEvents();
    } catch (error) {
        console.warn('[events] calendar not loaded', error);
        // After a return from the back/forward cache old live cards must not stay up.
        showLive(root, []);
        showPast(root, []);
        root.querySelector('[data-events-ld]')?.remove();
        upcomingList.replaceChildren(element('p', 'th-events-page__note', ERROR_NOTE));
        return;
    }
    const now = new Date();
    const sorted = splitByTime(events, now);
    showLive(root, sorted.live);
    showUpcoming(upcomingList, sorted.upcoming, now);
    showPast(root, sorted.past);
    root.querySelector('[data-events-months]').replaceChildren(...buildMonths(events, now));
    addStructuredData(root, [...sorted.live, ...sorted.upcoming]);
    revealLinkedEvent(root);
}

/**
 * The page is drawn after load, so the browser could not scroll to #event-… or #day-…
 * from a home chip by itself: open the past list if the target is there and scroll.
 */
function revealLinkedEvent(root) {
    if (revealedRoots.has(root)) {
        return;
    }
    revealedRoots.add(root);
    const id = decodeURIComponent(window.location.hash.slice(1));
    const target = id ? document.getElementById(id) : null;
    if (!target || !root.contains(target)) {
        return;
    }
    const past = target.closest('details');
    if (past) {
        past.open = true;
    }
    target.scrollIntoView({ block: 'start' });
}

/** Live, still ahead (including rumours and unconfirmed passed rumours) and over. */
function splitByTime(events, now) {
    const live = events.filter((event) => isLive(event, now));
    const rest = events.filter((event) => !isLive(event, now));
    return {
        live,
        upcoming: rest.filter((event) => !isOver(event, now)),
        past: rest.filter((event) => isOver(event, now)).reverse(),
    };
}

function showLive(root, events) {
    const section = root.querySelector('[data-events-live-section]');
    section.hidden = events.length === 0;
    root.querySelector('[data-events-live]').replaceChildren(...events.map(buildLiveCard));
}

function buildLiveCard(event) {
    const card = element('article', 'th-events-live');
    card.id = eventAnchor(event);
    const actions = element('p', 'th-events-live__actions');
    const stream = safeUrl(event.stream_url);
    if (stream) {
        actions.append(externalLink('th-events-live__watch', 'Watch stream', stream));
    }
    if (event.covered_live) {
        actions.append(externalLink('th-events-live__listen', 'Listen on thehype radio', RADIO_URL));
    }
    card.append(
        element('p', 'th-events-live__tag', 'Live'),
        element('h3', 'th-events-live__title', event.title),
        element('p', 'th-events-live__meta', `${event.organizer || ''} · ${formatStart(event)}`),
        actions,
    );
    return card;
}

function showUpcoming(list, events, now) {
    if (events.length === 0) {
        list.replaceChildren(
            element('p', 'th-events-page__note', EMPTY_NOTE),
            element('p', 'th-events-page__hint', EMPTY_HINT),
        );
        return;
    }
    const ahead = events.filter((event) => !isUnconfirmedPassed(event));
    const groups = [...groupByDay(ahead).entries()]
        .sort(([first], [second]) => first.localeCompare(second))
        .map(([key, dayEvents]) => buildDayGroup(key, dayEvents, now));
    const unconfirmed = events.filter(isUnconfirmedPassed);
    if (unconfirmed.length > 0) {
        groups.push(buildUnconfirmedGroup(unconfirmed));
    }
    list.replaceChildren(...groups);
}

/** Rumours whose day passed without confirmation — last, so they never lead the list. */
function buildUnconfirmedGroup(events) {
    const group = element('section', 'th-events-group');
    const cards = element('ol', 'th-events-group__cards');
    events.forEach((event) => cards.append(buildEventCard(event)));
    group.append(element('h3', 'th-events-group__date', 'Rumored date passed'), cards);
    return group;
}

function buildDayGroup(key, events, now) {
    const group = element('section', 'th-events-group');
    group.id = dayAnchor(key);
    const heading = element('h3', 'th-events-group__date', formatDay(key));
    if (key === dateKey(now)) {
        heading.append(element('span', 'th-events-group__today', 'Today'));
    }
    const cards = element('ol', 'th-events-group__cards');
    events.forEach((event) => cards.append(buildEventCard(event)));
    group.append(heading, cards);
    return group;
}

function buildEventCard(event) {
    const card = element('li', 'th-events-card');
    card.id = eventAnchor(event);
    const body = element('div', 'th-events-card__body');
    body.append(
        element('h4', 'th-events-card__title', event.title),
        buildCardMeta(event),
    );
    const dateNote = buildDateNote(event);
    if (dateNote) {
        body.append(dateNote);
    }
    if (Array.isArray(event.known) && event.known.length > 0) {
        body.append(buildKnown(event.known));
    }
    body.append(buildCardLinks(event));
    const time = hasExactDay(event) ? formatCardTime(event) : 'Date TBA';
    card.append(element('p', 'th-events-card__time', time), body);
    return card;
}

/** Rumour that passed shows its old day; the others show the start time. */
function formatCardTime(event) {
    return isUnconfirmedPassed(event) ? formatDay(dayKeyOf(event)) : formatStart(event);
}

function buildCardMeta(event) {
    const meta = element('p', 'th-events-card__meta');
    meta.append(element('span', '', event.organizer || ''));
    return meta;
}

/** A rumoured date says so and links its source; a passed rumour says it was not confirmed. */
function buildDateNote(event) {
    if (!isRumored(event) && !isUnconfirmedPassed(event)) {
        return null;
    }
    const text = isRumored(event) ? 'Rumored date' : 'Date passed, unconfirmed';
    const note = element('p', 'th-events-card__date-note', text);
    const source = safeUrl(event.date_source_url);
    if (source) {
        note.append(' · ', externalLink('th-events-card__source', 'Source ↗', source));
    }
    return note;
}

function buildKnown(facts) {
    const wrap = element('div', 'th-events-card__known');
    const list = element('ul', 'th-events-card__facts');
    facts.forEach((fact) => {
        const item = element('li', 'th-events-card__fact');
        item.dataset.kind = fact.kind === 'confirmed' ? 'confirmed' : 'expected';
        item.append(
            element('span', 'th-events-card__kind', item.dataset.kind),
            element('span', 'th-events-card__fact-text', fact.text || ''),
        );
        const source = safeUrl(fact.source_url);
        if (source) {
            item.append(externalLink('th-events-card__source', 'Source ↗', source));
        }
        list.append(item);
    });
    wrap.append(element('p', 'th-events-card__known-title', "What's known"), list);
    return wrap;
}

function buildCardLinks(event) {
    const links = element('p', 'th-events-card__links');
    const official = safeUrl(event.official_url);
    const stream = safeUrl(event.stream_url);
    if (official) {
        links.append(externalLink('th-events-card__link', 'Official site ↗', official));
    }
    if (stream) {
        links.append(externalLink('th-events-card__link', 'Stream ↗', stream));
    }
    return links;
}

function showPast(root, events) {
    const section = root.querySelector('[data-events-past-section]');
    section.hidden = events.length === 0;
    root.querySelector('[data-events-past]').replaceChildren(...events.map(buildPastItem));
}

function buildPastItem(event) {
    const item = element('li', 'th-events-past');
    item.id = eventAnchor(event);
    item.append(
        element('span', 'th-events-past__date', formatDay(dayKeyOf(event))),
        element('span', 'th-events-past__title', event.title),
        element('span', 'th-events-past__meta', event.organizer || ''),
    );
    if (event.covered_live) {
        item.append(element('span', 'th-events-past__covered', 'Covered live'));
    }
    return item;
}

function externalLink(className, text, url) {
    const link = element('a', className, text);
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener';
    return link;
}

/**
 * schema.org Event for every event with a day and a link — online events, the stream or
 * the official page is the location. Rumoured dates are left out: search engines would
 * show a guess as a fact.
 */
function addStructuredData(root, events) {
    root.querySelector('[data-events-ld]')?.remove();
    const items = events
        .filter((event) => hasExactDay(event) && !isRumored(event) && !isUnconfirmedPassed(event))
        .map(toSchemaEvent)
        .filter(Boolean);
    if (items.length === 0) {
        return;
    }
    const script = element('script', '', JSON.stringify({ '@context': 'https://schema.org', '@graph': items }));
    script.type = 'application/ld+json';
    script.dataset.eventsLd = '';
    root.append(script);
}

function toSchemaEvent(event) {
    const url = safeUrl(event.stream_url) || safeUrl(event.official_url);
    if (!url) {
        return null;
    }
    return {
        '@type': 'Event',
        name: event.title,
        startDate: event.starts_at && !event.time_tba ? event.starts_at : event.date,
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
        location: { '@type': 'VirtualLocation', url },
        organizer: { '@type': 'Organization', name: event.organizer || event.title },
        url: safeUrl(event.official_url) || url,
    };
}
